import React, { useEffect, useRef } from "react";

/**
 * VictoryCelebration, full-screen canvas particle animation
 * played when a kid wins a game. Only renders if victoryAnim is non-null
 * (i.e., the user has equipped one from the Arcade shop).
 *
 * Types supported:
 *  - confetti: cascading colored rectangles from top
 *  - fireworks: exploding radial particle bursts
 *  - rainbow: streamer arcs + sparkle shower
 */
const VictoryCelebration = ({ victoryAnim, duration = 3500, onDone }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!victoryAnim) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const W = canvas.width = window.innerWidth;
    const H = canvas.height = window.innerHeight;
    const colors = victoryAnim.colors || ["#F87171", "#FBBF24", "#34D399", "#60A5FA", "#A78BFA"];

    let particles = [];
    let explosions = [];
    let running = true;
    let rafId = null;
    const start = performance.now();

    const spawnConfetti = () => {
      for (let i = 0; i < (victoryAnim.particle_count || 60); i++) {
        particles.push({
          x: Math.random() * W,
          y: -20 - Math.random() * 200,
          vx: (Math.random() - 0.5) * 2,
          vy: 2 + Math.random() * 4,
          w: 6 + Math.random() * 8,
          h: 10 + Math.random() * 14,
          rot: Math.random() * Math.PI * 2,
          spin: (Math.random() - 0.5) * 0.3,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }
    };

    const spawnFirework = (cx, cy) => {
      const count = 45;
      const color = colors[Math.floor(Math.random() * colors.length)];
      for (let i = 0; i < count; i++) {
        const ang = (i / count) * Math.PI * 2;
        const speed = 3 + Math.random() * 4;
        explosions.push({
          x: cx, y: cy,
          vx: Math.cos(ang) * speed, vy: Math.sin(ang) * speed,
          life: 1, color,
        });
      }
    };

    const spawnRainbow = () => {
      // streamers from both sides
      for (let side = 0; side < 2; side++) {
        for (let i = 0; i < 30; i++) {
          particles.push({
            x: side === 0 ? -20 : W + 20,
            y: H / 2 + (Math.random() - 0.5) * H * 0.6,
            vx: side === 0 ? 3 + Math.random() * 5 : -(3 + Math.random() * 5),
            vy: (Math.random() - 0.5) * 3,
            w: 24 + Math.random() * 30,
            h: 3 + Math.random() * 2,
            rot: 0, spin: 0,
            color: colors[Math.floor(Math.random() * colors.length)],
            alpha: 1,
          });
        }
      }
      // sparkle shower
      for (let i = 0; i < 40; i++) {
        particles.push({
          x: Math.random() * W,
          y: -20 - Math.random() * 200,
          vx: (Math.random() - 0.5) * 1,
          vy: 3 + Math.random() * 4,
          w: 4, h: 4, rot: 0, spin: 0, star: true,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }
    };

    if (victoryAnim.type === "confetti") spawnConfetti();
    else if (victoryAnim.type === "rainbow") spawnRainbow();
    else if (victoryAnim.type === "fireworks") {
      // multiple firework bursts over time
      setTimeout(() => spawnFirework(W * 0.3, H * 0.4), 0);
      setTimeout(() => spawnFirework(W * 0.7, H * 0.5), 400);
      setTimeout(() => spawnFirework(W * 0.5, H * 0.3), 800);
      setTimeout(() => spawnFirework(W * 0.2, H * 0.6), 1200);
      setTimeout(() => spawnFirework(W * 0.8, H * 0.35), 1600);
      setTimeout(() => spawnFirework(W * 0.4, H * 0.55), 2000);
    }

    const step = (now) => {
      if (!running) return;
      const elapsed = now - start;
      ctx.clearRect(0, 0, W, H);

      // Update confetti / streamers
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.08;
        p.rot += p.spin;
        if (p.star) {
          ctx.fillStyle = p.color;
          ctx.globalAlpha = Math.max(0, 1 - elapsed / duration);
          ctx.beginPath();
          for (let k = 0; k < 5; k++) {
            const ang = (k / 5) * Math.PI * 2 - Math.PI / 2;
            const r = k % 2 === 0 ? 6 : 3;
            const x = p.x + Math.cos(ang) * r;
            const y = p.y + Math.sin(ang) * r;
            k === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          }
          ctx.closePath(); ctx.fill();
        } else {
          ctx.save();
          ctx.translate(p.x, p.y); ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = Math.max(0, 1 - elapsed / duration);
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        }
      });
      ctx.globalAlpha = 1;

      // Update fireworks explosions
      explosions = explosions.filter(e => e.life > 0);
      explosions.forEach(e => {
        e.x += e.vx; e.y += e.vy;
        e.vy += 0.08; e.life -= 0.018;
        ctx.fillStyle = e.color;
        ctx.globalAlpha = Math.max(0, e.life);
        ctx.beginPath();
        ctx.arc(e.x, e.y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      if (elapsed < duration) {
        rafId = requestAnimationFrame(step);
      } else {
        running = false;
        if (onDone) onDone();
      }
    };
    rafId = requestAnimationFrame(step);
    return () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [victoryAnim, duration, onDone]);

  if (!victoryAnim) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[9997]" data-testid="victory-celebration" aria-hidden="true">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
};

export default VictoryCelebration;
